import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CircleHelp, Play, X } from 'lucide-react';
import type { CurrentUserDto } from '@obralink/shared';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { cn } from '@/lib/cn';
import { Guia } from './guia';
import { ayudaDeLaZona } from './contexto';

/**
 * El botón de ayuda de abajo a la derecha: según la pantalla donde se esté,
 * enseña las preguntas frecuentes de esa zona.
 *
 *  · En el móvil se sube por encima de la barra de navegación inferior.
 *  · No se pinta en la propia página de Ayuda, donde ya está todo.
 *  · Cada guía se despliega dentro del panel; «Ver toda la ayuda» lleva a la
 *    página completa.
 *  · Al cambiar de pantalla el panel se cierra y se vuelve a calcular.
 */
export function AyudaFlotante({ user, esMovil }: { user: CurrentUserDto; esMovil: boolean }) {
  const { pathname } = useLocation();
  const [abierto, setAbierto] = useState(false);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());

  useEffect(() => {
    setAbierto(false);
    setAbiertas(new Set());
  }, [pathname]);

  if (pathname === '/ayuda') return null;

  const { titulo, guias } = ayudaDeLaZona(pathname, user);

  const alternar = (id: string) =>
    setAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      data-tour="ayuda-flotante"
      className={cn('fixed right-4 z-40', esMovil ? 'bottom-[4.75rem]' : 'bottom-5')}
    >
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={abierto ? 'Cerrar la ayuda' : 'Abrir la ayuda de esta pantalla'}
            className={cn(
              'flex size-12 items-center justify-center rounded-full border border-border-strong',
              'bg-brand text-fg-on-brand shadow-floating transition-transform hover:scale-105',
              'focus-visible:outline-2 focus-visible:outline-offset-2',
            )}
          >
            {abierto ? <X className="size-5" /> : <CircleHelp className="size-6" />}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          className="flex max-h-[min(34rem,calc(100vh-8rem))] w-[min(24rem,calc(100vw-2rem))] flex-col"
        >
          <div className="shrink-0 border-b border-border px-4 py-3">
            <p className="font-semibold text-fg">{titulo ? `Ayuda sobre ${titulo}` : 'Ayuda'}</p>
            <p className="text-xs text-fg-muted">Las dudas más habituales de esta pantalla.</p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {guias.length === 0 ? (
              <p className="px-4 py-6 text-sm text-fg-muted">
                No hay guías para esta pantalla. Prueba con la ayuda completa.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {guias.map((g) => (
                  <Guia
                    key={g.id}
                    pregunta={g}
                    abierta={abiertas.has(g.id)}
                    onAlternar={() => alternar(g.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-surface-2 px-4 py-2.5 text-sm">
            <Link
              to="/ayuda"
              onClick={() => setAbierto(false)}
              className="font-medium text-brand hover:underline"
            >
              Ver toda la ayuda
            </Link>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                window.dispatchEvent(new Event('obralink:tutorial'));
              }}
              className="flex items-center gap-1 text-fg-muted hover:text-fg"
            >
              <Play className="size-3.5" />
              Tutorial
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
